/**
 * FactRebalanceTimeline — таймлайн слоя «Фактический ребаланс» (DA-14933, таймлайн — DA-14953).
 *
 * Заменяет собой сайдбарные контролы даты и «Период суток». Два режима (radio
 * «Временной диапазон» в FactRebalanceFilter):
 *  - 'day'    — курсор на рельсе выбирает ДАТУ (сутки-агрегат), в шапке степпер ГОДА (‹ 2026 ›),
 *               рельса скоупится на выбранный год (границы = min/max доступных дат этого года).
 *  - 'period' — курсор на рельсе выбирает ПЕРИОД СУТОК (8 периодов), в шапке степпер ДАТЫ.
 *
 * Диапазон доступных дат берётся из MVT-слоя 'metadata' функции martin.fact_rebalance_parkings_mvt
 * (min_date/max_date), читается через getDateRangeFromTile. Курсор/дата/период пишутся в redux
 * (map.selectedRebalanceDate / selectedRebalancePeriod); перезапрос тайлов на смену даты/периода —
 * в Map.componentDidUpdate (query_param 'rebalance_date' / 'period').
 *
 * Монтируется только когда слой активен (withDisplayedLayersCheck).
 */
import React from 'react';
import { connect } from 'react-redux';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import * as T from '../../constants/types';
import { withDisplayedLayersCheck } from '../../HOC/withDisplayedLayersCheck';
import { getDateRangeFromTile } from '../../utils/dateRangeFromTile';
import { MapTimeline } from './MapTimeline';
import { DAY_PERIODS_DOMAIN, dayPeriodBands } from './presets';

const SOURCE_ID = 'martin.fact_rebalance_mvt';
const MS_DAY = 86400000;
// ISO 'YYYY-MM-DD' → номер дня (UTC) и обратно — целочисленный домен дней для рельсы.
const dayNum = iso => {
    if (!iso) return null;
    const [ y, m, d ] = iso.split('-').map(Number);
    return Math.floor(Date.UTC(y, m - 1, d) / MS_DAY);
};
const isoFromDay = n => new Date(n * MS_DAY).toISOString().slice(0, 10);
const fmtDDMM = iso => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
const FactRebalanceTimelineComponent = ({
    selectedRebalanceDate, selectedRebalancePeriod, selectedRebalanceDayMode,
    setSelectedRebalanceDate, setSelectedRebalancePeriod,
}) => {
    // eslint-disable-next-line no-underscore-dangle
    const map = typeof window !== 'undefined' ? window.__map : null;
    const [ range, setRange ] = React.useState(null); // { minDate, maxDate }

    // Диапазон доступных дат из metadata-слоя тайлов.
    React.useEffect(() => {
        if (!map) return () => {};
        const load = async () => {
            try {
                const r = await getDateRangeFromTile(map, SOURCE_ID);
                if (r && r.minDate && r.maxDate) setRange(r);
            } catch (err) {
                // оставляем прежний диапазон
            }
        };
        const onSourceData = e => { if (e.sourceId === SOURCE_ID && e.isSourceLoaded) load(); };
        const tid = setTimeout(load, 400);
        map.on('sourcedata', onSourceData);
        return () => { clearTimeout(tid); map.off('sourcedata', onSourceData) };
    }, [ map ]);

    if (!map) return null;

    // ─── Режим «Период»: суточная шкала (пресет), курсор по интервалам, степпер даты ───
    if (selectedRebalanceDayMode === 'period') {
        return (
            <MapTimeline
                domain={DAY_PERIODS_DOMAIN}
                cursor={selectedRebalancePeriod ?? 0}
                onCursorChange={idx => {
                    if (idx !== selectedRebalancePeriod) setSelectedRebalancePeriod(idx);
                }}
                minorTicks
                topBands={dayPeriodBands()}
                dateControl={{
                    value: selectedRebalanceDate,
                    onChange: setSelectedRebalanceDate,
                    minDate: range?.minDate,
                    maxDate: range?.maxDate,
                }}
            />
        );
    }

    // ─── Режим «Сутки»: курсор по датам года, степпер года ───
    // До загрузки диапазона — пустая рельса-заглушка.
    if (!range) {
        return (
            <MapTimeline
                domain={{ type: 'continuous', values: [ 0, 1 ], step: 1, format: () => '' }}
                cursor={0}
            />
        );
    }

    const minDay = dayNum(range.minDate);
    const maxDay = dayNum(range.maxDate);
    const minYear = Number(range.minDate.slice(0, 4));
    const maxYear = Number(range.maxDate.slice(0, 4));
    const years = [];
    for (let y = minYear; y <= maxYear; y += 1) years.push(y);

    // Год скоупа — из текущей даты (с клампом в доступный диапазон лет).
    let curDay = dayNum(selectedRebalanceDate);
    if (curDay === null || curDay < minDay || curDay > maxDay) curDay = maxDay;
    const year = Number(isoFromDay(curDay).slice(0, 4));
    const yi = years.indexOf(year);
    // Границы рельсы = весь календарный год (Январь–Декабрь), чтобы месячная сетка была
    // полной независимо от покрытия данными. Курсор на дату без данных просто даёт пустой
    // слой. minDay/maxDay здесь не ограничивают рельсу (нужны лишь для клампа курсора/лет).
    const yearMinDay = dayNum(`${year}-01-01`);
    const yearMaxDay = dayNum(`${year}-12-31`);
    if (curDay < yearMinDay) curDay = yearMinDay;
    if (curDay > yearMaxDay) curDay = yearMaxDay;

    // Степпер года: переход ставит дату на последний доступный день нового года.
    const goYear = delta => {
        const ny = years[yi + delta];
        if (!Number.isFinite(ny)) return;
        const nyMaxDay = Math.min(maxDay, dayNum(`${ny}-12-31`));
        setSelectedRebalanceDate(isoFromDay(nyMaxDay));
    };
    // Засечки таймлайна режима «Сутки»:
    //  - все 12 начал месяцев = точки (rc-slider dot); подпись (полное название) только на
    //    нечётных — Январь, Март, Май, Июль, Сентябрь, Ноябрь; на чётных точка без подписи;
    //  - недельные штрихи (minorTicks): каждый месяц делится на 4 равные части (3 штриха внутри).
    const monthStartDay = mIdx => dayNum(`${year}-${String(mIdx + 1).padStart(2, '0')}-01`);
    const monthName = mIdx => {
        const s = new Intl.DateTimeFormat(i18n.locale || 'ru', { month: 'long' }).format(new Date(year, mIdx, 1));
        return s.charAt(0).toUpperCase() + s.slice(1);
    };
    const inRail = d => d >= yearMinDay && d <= yearMaxDay;
    // Точки месяцев: подпись только на нечётных (0,2,…,10), на остальных — пустая (точка без текста).
    const monthTicks = Array.from({ length: 12 }, (_, mIdx) => ({
        value: monthStartDay(mIdx),
        label: mIdx % 2 === 0 ? monthName(mIdx) : '',
    })).filter(tk => inRail(tk.value));
    // Недельные штрихи: 3 внутримесячных деления (start + len·k/4, k=1..3).
    const weekTicks = [];
    for (let mIdx = 0; mIdx < 12; mIdx += 1) {
        const start = monthStartDay(mIdx);
        const next = mIdx < 11 ? monthStartDay(mIdx + 1) : dayNum(`${year + 1}-01-01`);
        const len = next - start;
        for (let k = 1; k < 4; k += 1) {
            const d = Math.round(start + (len * k) / 4);
            if (inRail(d)) weekTicks.push(d);
        }
    }
    const domain = {
        type: 'continuous',
        values: [ yearMinDay, yearMaxDay ],
        step: 1,
        format: n => fmtDDMM(isoFromDay(Math.round(n))),
        ticks: monthTicks,
    };

    return (
        <MapTimeline
            domain={domain}
            cursor={curDay}
            onCursorChange={n => {
                const iso = isoFromDay(Math.round(n));
                if (iso !== selectedRebalanceDate) setSelectedRebalanceDate(iso);
            }}
            minorTicks={weekTicks}
            stepperControl={{
                display: String(year),
                onPrev: () => goYear(-1),
                onNext: () => goYear(1),
                prevDisabled: yi <= 0,
                nextDisabled: yi >= years.length - 1,
            }}
        />
    );
};
const mapStateToProps = ({ map: { selectedRebalanceDate, selectedRebalancePeriod, selectedRebalanceDayMode } }) => ({
    selectedRebalanceDate, selectedRebalancePeriod, selectedRebalanceDayMode,
});
const mapDispatchToProps = dispatch => ({
    setSelectedRebalanceDate: date => dispatch({
        type: T.SET_SELECTED_REBALANCE_DATE,
        payload: { selectedRebalanceDate: date },
    }),
    setSelectedRebalancePeriod: period => dispatch({
        type: T.SET_SELECTED_REBALANCE_PERIOD,
        payload: { selectedRebalancePeriod: period },
    }),
});
const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(FactRebalanceTimelineComponent);

export const FactRebalanceTimeline = withDisplayedLayersCheck(
    ConnectedComponent,
    t`Фактический ребаланс`
);
